/**
 * 路由守卫配置
 *
 * 功能：
 * - 登录鉴权
 * - 动态路由加载
 * - 权限验证
 * - 页面标题设置
 * - 进度条显示
 */
import type { Router } from "vue-router";
import NProgress from "nprogress";
import { useUserStore } from "@/stores/user";
import { usePermissionStore } from "@/stores/permission";
import { notFoundRoute } from "./routes";
import { addRoutes } from "./index";

// 配置 NProgress
NProgress.configure({ showSpinner: false });

// 白名单路由（不需要登录即可访问）
const whiteList = ["/login", "/404", "/403"];

/**
 * 配置路由守卫
 */
export function setupRouterGuard(router: Router) {
  // 全局前置守卫
  router.beforeEach(async (to, _, next) => {
    // 开始进度条
    NProgress.start();

    const userStore = useUserStore();
    const permissionStore = usePermissionStore();

    // 设置页面标题
    document.title = "LuckyTask Admin";

    // 检查是否已登录
    if (userStore.isLoggedIn) {
      // 已登录，禁止访问登录页
      if (to.path === "/login") {
        next({ path: "/" });
        NProgress.done();
        return;
      }

      // 检查是否已加载动态路由
      // 使用 dynamicRoutes 判断，避免与 constantRoutes 混淆
      if (permissionStore.dynamicRoutes.length === 0) {
        try {
          console.log("[Router Guard] 开始加载动态路由...");

          // 始终获取最新的用户信息和权限
          // 即使登录时保存了基本信息，也要获取完整的权限数据
          console.log("[Router Guard] 获取用户信息和权限...");
          await userStore.getUserInfo();

          console.log("[Router Guard] 用户角色:", userStore.roles);
          console.log(
            "[Router Guard] 用户权限数量:",
            userStore.permissions.length
          );

          // 生成动态路由
          const accessRoutes = await permissionStore.generateRoutes(
            userStore.roles,
            userStore.permissions
          );

          console.log(
            "[Router Guard] 动态路由生成完成，准备添加路由:",
            accessRoutes
          );

          // 检查是否成功生成路由
          if (!accessRoutes || accessRoutes.length === 0) {
            console.warn(
              "[Router Guard] 警告：动态路由为空，可能是菜单数据未同步"
            );
            throw new Error("动态路由生成失败：菜单数据为空");
          }

          // 添加动态路由
          addRoutes(accessRoutes);

          // 添加 404 路由（必须在最后）
          router.addRoute(notFoundRoute);

          console.log("[Router Guard] 路由添加完成，重定向到:", to.path);

          // 重定向到目标路由
          const redirectPath = to.redirectedFrom?.fullPath || to.fullPath;
          next({ path: redirectPath, replace: true });
        } catch (error) {
          console.error("[Router Guard Error]:", error);

          // 显示错误提示
          if (error instanceof Error) {
            console.error("[Router Guard] 错误详情:", error.message);
          }

          // 加载路由失败，清除状态并跳转到登录页
          permissionStore.resetRoutes();
          await userStore.logout();

          // 添加错误信息到 redirect URL
          const errorMsg =
            encodeURIComponent("路由加载失败，请重新登录或联系管理员");
          next(`/login?redirect=${to.path}&error=${errorMsg}`);
          NProgress.done();
        }
      } else {
        // 已加载动态路由，直接放行
        next();
      }
    } else {
      // 未登录
      if (whiteList.includes(to.path)) {
        // 白名单路由，直接放行
        next();
      } else {
        // 跳转到登录页
        next(`/login?redirect=${to.path}`);
        NProgress.done();
      }
    }
  });

  // 全局后置守卫
  router.afterEach(() => {
    // 结束进度条
    NProgress.done();
  });

  // 全局错误处理
  router.onError((error) => {
    console.error("[Router Error]:", error);
    NProgress.done();
  });
}
